/**
 * One People-tab search result: avatar, display name, `@username`, and a
 * trailing button. Props-driven, calls no hook, so it is fully testable
 * before ISSUE-189 ships.
 *
 * `min-w-0` on the row and `min-w-0 flex-1` on the name column are required,
 * not decoration. ISSUE-193's execution note records its member row pushing
 * `scrollWidth` to 420px against a 390px viewport for exactly this anatomy;
 * WI-11 asserts the fix.
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

import type { typeInviteResultRow } from "./inviteResultRows";
import {
  INVITE_ROW_ACTION_LABEL,
  INVITE_ROW_IN_POOL_LABEL,
  INVITE_ROW_INVITED_LABEL,
} from "./inviteSheetCopy";

export type typeInviteResultRowProps = {
  row: typeInviteResultRow;
  isBusy: boolean;
  onInvite: (userId: string) => void;
};

export const InviteResultRow = ({
  row,
  isBusy,
  onInvite,
}: typeInviteResultRowProps): ReactNode => (
  <div
    className="flex min-w-0 items-center gap-3"
    data-testid={`invite-result-${row.userId}`}
  >
    {row.avatarUrl !== null ? (
      <img src={row.avatarUrl} alt="" className="h-icon-lg w-icon-lg" />
    ) : null}
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="text-body-strong text-ink break-all">
        {row.displayName}
      </span>
      <span className="text-caption text-ink-secondary break-all">
        {row.handle}
      </span>
    </div>
    {row.action === "invite" ? (
      <Button
        variant="secondary"
        size="sm"
        loading={isBusy}
        loadingLabel={INVITE_ROW_ACTION_LABEL}
        onClick={() => {
          onInvite(row.userId);
        }}
        className="ml-auto"
        data-testid={`invite-action-${row.userId}`}
      >
        {INVITE_ROW_ACTION_LABEL}
      </Button>
    ) : null}
    {row.action === "invited" ? (
      <Button
        variant="secondary"
        size="sm"
        disabled
        className="ml-auto"
        data-testid={`invite-action-${row.userId}`}
      >
        {INVITE_ROW_INVITED_LABEL}
      </Button>
    ) : null}
    {row.action === "inPool" ? (
      <span
        className="ml-auto text-caption text-ink-tertiary"
        data-testid={`invite-state-${row.userId}`}
      >
        {INVITE_ROW_IN_POOL_LABEL}
      </span>
    ) : null}
  </div>
);
